import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import type { Lang } from "../lib/i18n";
import { pick } from "../lib/i18n";
import { stepVariants, respectMotion } from "../lib/motion";
import { getPolicyChapters, subscribePolicyChapters, chapterTitle, chapterIntro, chapterBodyHtml } from "../data/policies";
import { sanitizeHtml } from "../data/qa";
import { currentEmail, loadProgress, saveProgress } from "../lib/progress";

// How long a chapter must stay open before it counts as genuinely read. Real
// engagement, not a formality — this is the one mandatory-reading step in the
// whole journey, covering official company policy.
const READ_DWELL_MS = 5000;

/** Step 4 — collapsible HR policy chapters (admin-editable content); all must
 *  be genuinely read (kept open a few seconds each, not just clicked) to continue. */
export function PoliciesScreen({ lang, onNext, onBack }: { lang: Lang; onNext: () => void; onBack: () => void }) {
  const t = (ar: string, en: string) => pick(lang, ar, en);
  const email = useMemo(() => currentEmail(), []);
  const saved = useMemo(() => (email ? loadProgress(email) : null), [email]);
  const [chapters, setChapters] = useState(() => getPolicyChapters());
  useEffect(() => subscribePolicyChapters(() => setChapters(getPolicyChapters())), []);

  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [read, setRead] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    (saved?.readChapters || []).forEach((id) => (initial[id] = true));
    return initial;
  });
  const [pending, setPending] = useState<Record<string, boolean>>({});
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const allRead = useMemo(() => chapters.length > 0 && chapters.every((c) => read[c.id]), [chapters, read]);

  // Persist read chapters as they're confirmed, so leaving mid-way and coming
  // back doesn't force re-reading chapters already genuinely reviewed.
  useEffect(() => {
    if (!email) return;
    saveProgress(email, { readChapters: Object.keys(read).filter((id) => read[id]) });
  }, [read, email]);

  useEffect(() => {
    const activeTimers = timers.current;
    return () => {
      Object.values(activeTimers).forEach(clearTimeout);
    };
  }, []);

  function startReadTimer(id: string) {
    if (read[id] || timers.current[id]) return;
    setPending((p) => ({ ...p, [id]: true }));
    timers.current[id] = setTimeout(() => {
      setRead((r) => ({ ...r, [id]: true }));
      setPending((p) => ({ ...p, [id]: false }));
      delete timers.current[id];
    }, READ_DWELL_MS);
  }
  function cancelReadTimer(id: string) {
    if (!timers.current[id]) return;
    clearTimeout(timers.current[id]);
    delete timers.current[id];
    setPending((p) => ({ ...p, [id]: false }));
  }

  // Open the first chapter (and start its timer) once chapters are known.
  const firstId = chapters[0]?.id;
  useEffect(() => {
    if (!firstId) return;
    setOpen((prev) => (prev[firstId] !== undefined ? prev : { ...prev, [firstId]: true }));
    startReadTimer(firstId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstId]);

  function toggle(id: string) {
    setOpen((prev) => {
      const isOpening = !prev[id];
      if (isOpening) startReadTimer(id);
      else cancelReadTimer(id);
      return { ...prev, [id]: isOpening };
    });
  }
  function expandAll() {
    const all: Record<string, boolean> = {};
    chapters.forEach((c) => {
      all[c.id] = true;
      startReadTimer(c.id);
    });
    setOpen(all);
  }

  return (
    <motion.div
      className="kd-step-container kd-policies-step"
      dir={lang === "ar" ? "rtl" : "ltr"}
      variants={respectMotion(stepVariants)}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      <div className="glass-panel">
        <div className="kd-step-header">
          <h1>{t("سياسات وإجراءات كادن", "Kaden's Policies & Procedures")}</h1>
          <p>
            {t(
              "صُمم هذا الدليل ليكون مرجعك العملي، يوضح أهم السياسات والإجراءات والمزايا المعتمدة، ويجيب عن أكثر الأسئلة شيوعًا، ليساعدك على أداء عملك بثقة ووضوح.",
              "This guide is designed to be your practical reference, it outlines the key approved policies, procedures, and benefits, and answers the most common questions, so you can do your job with confidence and clarity.",
            )}
          </p>
        </div>

        <div className="kd-policy-toolbar">
          <button type="button" className="kd-policy-expand-all" onClick={expandAll}>
            {t("توسيع كل الفصول", "Expand all chapters")}
          </button>
        </div>

        <div className="kd-policy-accordion">
          {chapters.map((c) => {
            const isOpen = !!open[c.id];
            const isRead = !!read[c.id];
            const isPending = !!pending[c.id];
            return (
              <section className={`kd-policy-acc-item${isOpen ? " is-open" : ""}${isRead ? " is-read" : ""}`} key={c.id}>
                <button
                  type="button"
                  className="kd-policy-acc-header"
                  onClick={() => toggle(c.id)}
                  aria-expanded={isOpen}
                  aria-controls={`policy-panel-${c.id}`}
                >
                  <span className={`kd-policy-checkbox${isRead ? " is-checked" : ""}${isPending ? " is-pending" : ""}`} aria-hidden>
                    {isRead && (
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                    {isPending && !isRead && <span className="kd-policy-checkbox-spinner" />}
                  </span>
                  <span className="kd-policy-acc-icon" aria-hidden>{c.icon}</span>
                  <span className="kd-policy-acc-title">{chapterTitle(c, lang)}</span>
                  {isPending && !isRead && (
                    <span className="kd-policy-reading-hint">{t("جارٍ التسجيل كمقروء…", "Marking as read…")}</span>
                  )}
                  <span className={`kd-policy-acc-caret${isOpen ? " is-open" : ""}`} aria-hidden>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      id={`policy-panel-${c.id}`}
                      className="kd-policy-acc-panel"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.22, ease: "easeInOut" }}
                    >
                      <div className="kd-policy-acc-inner">
                        <p className="kd-policy-acc-intro">{chapterIntro(c, lang)}</p>
                        <div className="kd-policy-body kd-rich-content" dangerouslySetInnerHTML={{ __html: sanitizeHtml(chapterBodyHtml(c, lang)) }} />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </section>
            );
          })}
        </div>

        <div className="kd-policy-actions">
          <button className="kd-btn-secondary" onClick={onBack}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <polyline points={lang === "ar" ? "9 18 15 12 9 6" : "15 18 9 12 15 6"} />
            </svg>
            <span>{t("السابق", "Back")}</span>
          </button>
          {!allRead && (
            <span className="kd-policy-progress-hint">
              {t(
                `متبقٍ ${chapters.length - Object.keys(read).filter((id) => read[id]).length} من ${chapters.length} فصول`,
                `${chapters.length - Object.keys(read).filter((id) => read[id]).length} of ${chapters.length} chapters remaining`,
              )}
            </span>
          )}
          <button className="kd-btn-primary" onClick={onNext} disabled={!allRead}>
            <span>{t("تابع الرحلة", "Continue journey")}</span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <polyline points={lang === "ar" ? "15 18 9 12 15 6" : "9 18 15 12 9 6"} />
            </svg>
          </button>
        </div>
      </div>
    </motion.div>
  );
}
